import React, {useCallback, useEffect, useState} from 'react';
import i18n from '@/i18n';
import {validateInviteCode} from './invitation.service';
import {useConfirm, BindingFailedIcon} from '@/components/basic/modal';
import {removeChineseCharacters} from '@/utils/text';

export type ShowInvalidInviteCode = (message: string) => void;
const invalidInviteCodeMessage = 'Incorrect verification code';

export function filterInviteCodeInput(value: string) {
  return removeChineseCharacters(value);
}

export function clearInvalidInviteCodeStorage() {
  localStorage.removeItem('invitationCode');
}

export function useIsInviteCodeBlocked(inviteCode?: string) {
  const [isInviteCodeBlocked, setIsInviteCodeBlocked] = useState<
    boolean | null
  >(null);

  useEffect(() => {
    const code = inviteCode?.trim();
    if (!code) {
      setIsInviteCodeBlocked(null);
      return;
    }

    let active = true;
    setIsInviteCodeBlocked(null);
    validateInviteCode(code)
      .then(result => {
        if (!active) {
          return;
        }
        const isValid = result?.valid === true || result?.status === 'VALID';
        setIsInviteCodeBlocked(!isValid);
      })
      .catch(() => {
        if (active) {
          // 与原代理中心逻辑一致：仅校验请求失败时不阻断重置功能。
          setIsInviteCodeBlocked(false);
        }
      });

    return () => {
      active = false;
    };
  }, [inviteCode]);

  return isInviteCodeBlocked;
}

const getLocationInviteCode = () => {
  if (typeof window === 'undefined') {
    return '';
  }
  return new URLSearchParams(window.location.search).get('code')?.trim() || '';
};

export function getInitialSignUpInviteCode(routeCode?: string | null) {
  const queryCode = routeCode?.trim() || getLocationInviteCode();
  if (queryCode) {
    return filterInviteCodeInput(queryCode);
  }
  return filterInviteCodeInput(
    localStorage.getItem('invitationCode')?.trim() || '',
  );
}

export function getInitialInvitationInviteCode(routeCode?: string | null) {
  return filterInviteCodeInput(routeCode?.trim() || getLocationInviteCode());
}

export async function resolveInviteCode(
  value: string | null | undefined,
  showInvalidInviteCode?: ShowInvalidInviteCode,
  onInvalid?: () => void,
): Promise<string> {
  const code = value?.trim();
  if (!code) {
    return '';
  }

  try {
    const result = await validateInviteCode(code);
    if (result?.valid === true || result?.status === 'VALID') {
      return code;
    }
    showInvalidInviteCode?.(
      result?.message?.trim() || invalidInviteCodeMessage,
    );
  } catch {
    showInvalidInviteCode?.(invalidInviteCodeMessage);
  }

  clearInvalidInviteCodeStorage();
  onInvalid?.();
  return '';
}

export function useInvalidInviteCodeConfirm() {
  const {renderModal, show, hide} = useConfirm(undefined, undefined, {
    variant: 'error',
    icon: React.createElement(BindingFailedIcon, {width: 28, height: 28}),
    duration: 5000,
    closeOnClick: true,
  });

  const showInvalidInviteCode = useCallback(
    (message: string) => {
      show(
        i18n.t('inviteCode.bindingFailed'),
        message,
        undefined,
        undefined,
        true,
      );
    },
    [show],
  );

  return {
    renderInvalidCodeModal: renderModal,
    showInvalidInviteCode,
    hideInvalidInviteCode: hide,
  };
}
